import React, { FC, HTMLAttributes, useEffect, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import cn from 'classnames';
import { FaVolumeMute } from 'react-icons/fa';
import { isIOS, isAndroid } from '~/utils/platform';

interface VideoPlayerPropsInferface extends HTMLAttributes<HTMLVideoElement> {
  videoJsOptions: videojs.PlayerOptions;
  timestamp?: number;
  tryToAutoplay?: boolean;
  hidePlayButton?: boolean;
}

const VideoPlayer: FC<VideoPlayerPropsInferface> = ({
  videoJsOptions,
  timestamp,
  tryToAutoplay = false,
  hidePlayButton = false,
  ...videoProps
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const videoElementRef = React.useRef<HTMLVideoElement | null>(null);
  const videoPlayerRef = React.useRef<VideoJsPlayer | null>(null);

  useEffect(() => {
    const videoElement = videoElementRef.current;
    const videoPlayer = videoPlayerRef.current;

    if (!videoPlayer && videoElement) {
      const videoJsInstance = videojs(videoElement, {
        autoplay: false,
        muted: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        playsinline: true, // For mobile Safari
        retryOnError: true,
        controls: true,
        controlBar: {
          volumePanel: true,
          fullscreenToggle: false,
          pictureInPictureToggle: false,
          currentTimeDisplay: true,
          remainingTimeDisplay: false,
          playToggle: !hidePlayButton,
          captionsButton: false,
        },
        // Disable the "CC" button on Safari
        html5: { nativeTextTracks: false },
        ...videoJsOptions,
      });

      videoJsInstance.ready(() => {
        videoJsInstance.on('volumechange', () => {
          setIsMuted(!!videoPlayerRef?.current?.muted());
        });

        videoJsInstance.on('play', () => {
          setIsPlaying(true);
        });

        videoJsInstance.on('pause', () => {
          setIsPlaying(false);
        });

        if (tryToAutoplay) {
          // Programmatic autoplay
          const promise = videoJsInstance.play();

          if (promise !== undefined) {
            promise.catch(e => {
              console.error('Unable to autoplay', e);
            });
          }
        }
      });

      videoPlayerRef.current = videoJsInstance;
    }
  }, [hidePlayButton, tryToAutoplay, videoJsOptions]);

  useEffect(() => {
    if (videoPlayerRef.current && timestamp !== undefined) {
      videoPlayerRef.current.currentTime(timestamp);
    }
  }, [timestamp]);

  const handleUnmute = () => {
    videoPlayerRef?.current?.muted(false);
  };

  const isMobileDevice = isIOS() || isAndroid();

  return (
    <>
      {isPlaying && isMuted && (
        <div
          className={
            'flex items-center justify-center absolute inset-0 pointer-events-none z-10 text-white'
          }
        >
          <button
            className={
              'flex px-5 py-4 bg-black bg-opacity-40 text-base font-semibold pointer-events-auto rounded-lg relative bottom-8 items-center'
            }
            onClick={handleUnmute}
          >
            <FaVolumeMute size={'24px'} />{' '}
            <span className={'ml-2'}>
              {isMobileDevice ? 'Tap' : 'Click'} to unmute
            </span>
          </button>
        </div>
      )}

      <div data-vjs-player="true">
        <video
          ref={videoElementRef}
          data-setup='{ "inactivityTimeout": 0 }'
          {...videoProps}
          className={cn('video-js vjs-big-play-centered', videoProps.className)}
        />
      </div>
    </>
  );
};

export default VideoPlayer;
